import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './ui.module.css';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANT: Record<Variant, string> = {
  primary: styles.btnPrimary ?? '',
  secondary: styles.btnSecondary ?? '',
  ghost: styles.btnGhost ?? '',
  danger: styles.btnDanger ?? '',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  block?: boolean;
  size?: 'sm' | 'md';
}

export function Button({
  variant = 'primary',
  block,
  size = 'md',
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  const cls = [
    styles.btn,
    VARIANT[variant],
    block ? styles.btnBlock : '',
    size === 'sm' ? styles.btnSm : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <button type={type} className={cls} {...rest} />;
}

export function Card({
  title,
  children,
  className,
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={[styles.card, className ?? ''].filter(Boolean).join(' ')}>
      {title != null && <h2 className={styles.cardTitle}>{title}</h2>}
      {children}
    </section>
  );
}

type AlertKind = 'info' | 'success' | 'warning' | 'danger';
const ALERT: Record<AlertKind, string> = {
  info: styles.alertInfo ?? '',
  success: styles.alertSuccess ?? '',
  warning: styles.alertWarning ?? '',
  danger: styles.alertDanger ?? '',
};

export function Alert({ kind = 'info', children }: { kind?: AlertKind; children: ReactNode }) {
  return (
    <div className={`${styles.alert} ${ALERT[kind]}`} role={kind === 'danger' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Badge({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: 'success' | 'warning';
}) {
  const cls = [
    styles.badge,
    tone === 'success' ? styles.badgeSuccess : '',
    tone === 'warning' ? styles.badgeWarning : '',
  ]
    .filter(Boolean)
    .join(' ');
  return <span className={cls}>{children}</span>;
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className={styles.empty}>{children}</p>;
}

export function Spinner() {
  return <span className={styles.spinner} role="status" aria-label="Carregando" />;
}
